import { Text } from '@oneaudi/unified-web-components';
import { useMemo, useState } from 'react';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { ImageSelectionTile } from '~/components/image-selection-tile/components/image-selection-tile';
import { TileInformation } from '~/components/image-selection-tile/components/tile-information';
import {
	SubFamilyContainer,
	TileContainer,
} from '~/components/image-selection-tile/style/tile-selection-section-style';
import { Prices } from '~/components/prices';
import { RenderHtml } from '~/components/text';
import { PriceFragment } from '~/graphql/__generated__/graphql';
import { FilteredItem } from '~/graphql/types';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';

interface SubFamilyProps {
	name: string;
	items: FilteredItem[];
	isLoading: boolean;
	handleItemSelection: (item: FilteredItem) => void;
	selectedItem?: FilteredItem;
	fallbackSVG: JSX.Element;
}

export const SubFamily = ({
	name,
	items,
	isLoading,
	handleItemSelection,
	selectedItem,
	fallbackSVG,
}: SubFamilyProps) => {
	const [displayedItem, setDisplayedItem] = useState<FilteredItem | null>(null);
	const vatConfig = useVATConfig();
	const selectedItemInFamily = items.find((item) => item.name === selectedItem?.name);
	const { formatCurrency } = useCurrencyFormatter();

	const itemWithLowestPrice = useMemo(() => {
		// Find the item with the lowest price
		if (items.length === 0) return null;

		return items.reduce((lowest, item) => {
			if (item.price?.totalPrice?.__typename === 'Price' && lowest.price?.totalPrice?.__typename === 'Price') {
				return item.price.totalPrice.value < lowest.price.totalPrice.value ? item : lowest;
			}
			return lowest;
		}, items[0]);
	}, [items]);

	const groupPrice =
		itemWithLowestPrice &&
		formatCurrency(itemWithLowestPrice.price?.totalPrice as PriceFragment, undefined, 'min-price');

	return (
		<SubFamilyContainer>
			<Text className="family-title" tag="p" variant={{ type: 'copy', order: '1', style: 'normal' }}>
				<RenderHtml html={name} />
			</Text>
			{!selectedItemInFamily && groupPrice && (
				<div className="family-price">
					{displayedItem ? (
						<Prices
							size="3"
							totalPrice={displayedItem.price?.totalPrice}
							monthlyRate={displayedItem.price?.monthlyRate}
							vatConfig={vatConfig.feature}
						/>
					) : (
						<Text tag="p" variant={{ type: 'copy', order: '3', style: 'normal' }}>
							{groupPrice}
						</Text>
					)}
				</div>
			)}

			<div className="inner-container">
				{items.map((item) => (
					<TileContainer key={item.key}>
						<ImageSelectionTile
							altText={`${item.familyName} - ${item.name}`}
							isLoading={item.loading}
							isSelected={item.selected}
							backgroundImage={item.imgUrl}
							disabled={isLoading || item.loading}
							onClick={() => handleItemSelection(item)}
							onMouseEnter={() => setDisplayedItem(item)}
							onMouseLeave={() => setDisplayedItem(null)}
							fallbackSVG={fallbackSVG}
						/>
						<TileInformation item={item} />
					</TileContainer>
				))}
			</div>
		</SubFamilyContainer>
	);
};
